import { Component, Input, OnInit } from '@angular/core';
import { Directions, SimulatorConfig } from '../../core/models/simulation.model';
import { Destroyable } from '../../utils/mixins/destroyable.mixin';

import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { SimulatorType } from '../../core/enums/simulator-type.enum';
import { Router } from '@angular/router';
import { SimulationsService } from '../../services/simulations/simulations.service';
import { SnackbarService } from '../../services/snackbar.service';
import { LoadingService } from '../../services/loading.service';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { COMPONENT_ID_HEADER } from '../../core/consts/api.const';
import { MatDialog } from '@angular/material/dialog';
import { SelectUpscalingRockImagePopupComponent } from '../popups/select-upscaling-rock-image-popup/select-upscaling-rock-image-popup.component';
import { SelectRockModelPopupComponent } from '../popups/select-rock-model-popup/select-rock-model-popup.component';
import { filter } from 'rxjs';
import { UpscalingRockFile } from '../../core/models/upscaling-rock-files.model';
import { RockModel } from '../../core/models/rock-model.model';
import { RemoveExtensionPipe } from '../pipes/remove-extension.pipe';
import { RockThumbnailService } from '../../services/rock-thumbnail/rock-thumbnail.service';
import { RockImagesService } from '../../services/rock-images/rock-images.service';
import { RockFileLoader } from '../../utils/rock-file-loader.util';
import { RockModelsService } from '../../services/rock-models/rock-models.service';
import { RockPreviewComponent } from '../rock-preview/rock-preview.component';

@Component({
  selector: 'app-basic-upscaling-simulator',
  imports: [
    FormsModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSnackBarModule,
    MatCheckboxModule,
    ReactiveFormsModule,
    RockPreviewComponent
],
  templateUrl: './basic-upscaling-simulator.component.html',
  providers:[RemoveExtensionPipe],
  styleUrl: './basic-upscaling-simulator.component.scss',
  standalone: true
})
export class BasicUpscalingSimulatorComponent extends Destroyable(Object) implements OnInit {
  @Input() config!: SimulatorConfig;

  simulationDirectionsForm!: FormGroup;
  simulationNameForm!: FormGroup;
  fileName!: FormControl;
  rockModelControl!: FormControl;
  isLoading: boolean = false;
  thumbnailUrl: string | null = null;
  rockFileMetadata: UpscalingRockFile | null = null;
  availableRockModels: RockModel[] = [];
  isLoadingRockModels: boolean = false;
  
  private rockFileLoader: RockFileLoader;

  constructor(
    private readonly router: Router,
    private readonly fb: FormBuilder,
    private readonly dialog: MatDialog,
    private readonly simulationsService: SimulationsService,
    private readonly snackbarService: SnackbarService,
    private readonly loadingService: LoadingService,
    private readonly removeExtensionPipe: RemoveExtensionPipe,
    private readonly rockThumbnailService: RockThumbnailService,
    private readonly rockImagesService: RockImagesService,
    private readonly rockModelsService: RockModelsService,
  ) {
    super();
    this.rockFileLoader = new RockFileLoader(rockThumbnailService, rockImagesService);
  }

  get totalPorosity(): string {
    const selectedModel = this.rockModelControl?.value as RockModel | null;
    if (selectedModel && selectedModel.totalPorosity != null) {
      return selectedModel.totalPorosity.toFixed(2);
    }
    return 'N/A';
  }

  get shouldShowPorosity(): boolean {
    return !!this.rockModelControl?.value;
  }

  get componentId(): SimulatorType {
    return this.config.simulatorType;
  }

  get nameControl(): FormControl {
    return this.simulationNameForm.get('name') as FormControl;
  }

  get rockModelName(): string {
    const selectedModel = this.rockModelControl.value as RockModel | null;
    return selectedModel ? selectedModel.name : '';
  }

  ngOnInit(): void {
    this.initSimulationNameForm();
    this.initSimulationDirectionsForm();
    this.initFileName();
    this.initRockModelControl();
    this.subscribeToLoadingState();
  }

  selectRockFileName(): void {
    const dialogRef = this.dialog.open(SelectUpscalingRockImagePopupComponent, {
      panelClass: 'large-popup-size'
    });

    dialogRef.afterClosed()
      .pipe(
        filter(Boolean),
        this.takeUntilDestroyed()
      )
      .subscribe((file: UpscalingRockFile) => {
        this.fileName.setValue(this.removeExtensionPipe.transform(file.fileName));
        this.rockFileMetadata = file;
        this.loadThumbnail(file.fileName);
        this.updateAvailableRockModels(file);
      });
  }

  selectRockModel(): void {
    if (!this.fileName.value) {
      this.snackbarService.openCustom({
        message: 'Please select an upscaling image first',
        title: 'Warning',
        type: 'warning',
        action: 'Close',
      });
      return;
    }

    const dialogRef = this.dialog.open(SelectRockModelPopupComponent, {
      panelClass: 'large-popup-size',
      data: { rockModels: this.availableRockModels }
    });

    dialogRef.afterClosed()
      .pipe(
        filter(Boolean),
        this.takeUntilDestroyed()
      )
      .subscribe((model: RockModel) => {
        this.rockModelControl.setValue(model);
      });
  }

  private updateAvailableRockModels(file: UpscalingRockFile): void {
    // Reset rock models and disable control while loading
    this.availableRockModels = [];
    this.rockModelControl.setValue(null);
    this.rockModelControl.disable();
    this.isLoadingRockModels = true;
    
    // Fetch rock models from the API
    const options = {
      headers: new HttpHeaders({
        [COMPONENT_ID_HEADER]: this.componentId,
      }),
    };
    
    this.rockModelsService
      .getUpscalingRockFileModels(file.fileName, options)
      .pipe(this.takeUntilDestroyed())
      .subscribe({
        next: (rockModels) => {
          // Map UpscalingRockFileModelShortDto[] to RockModel[]
          this.availableRockModels = rockModels.map(model => ({
            id: model.id,
            name: model.name,
            totalPorosity: model.totalPorosity,
            permeability: model.permeability ? 'Selected' : 'Not selected',
            formationFactor: model.formationFactor ? 'Selected' : 'Not selected',
          }));
          
          this.isLoadingRockModels = false;
          
          // Enable control if rock models are available
          if (this.availableRockModels.length > 0) {
            this.rockModelControl.enable();
          } else {
            // Show notification when no rock models are found
            this.snackbarService.openCustom({
              message: 'No rock models found for this upscaling image.',
              title: 'Info',
              type: 'info',
              action: 'Close',
            });
          }
        },
        error: (error) => {
          this.isLoadingRockModels = false;
          this.snackbarService.openCustom({
            message: 'Failed to load rock models for the selected file',
            title: 'Error',
            type: 'error',
            action: 'Close',
          });
          console.error('Error loading rock models:', error);
        }
      });
  }

  private loadThumbnail(fileName: string): void {
    const previousThumbnailUrl = this.thumbnailUrl;

    // Load thumbnail for upscaling image type
    this.rockFileLoader
      .getThumbnail(fileName, 'Upscaling')
      .pipe(this.takeUntilDestroyed())
      .subscribe((url) => {
        if (url) {
          if (previousThumbnailUrl) {
            this.rockFileLoader.revokeThumbnailUrl(previousThumbnailUrl);
          }
          this.thumbnailUrl = url;
        }
      });
    // Note: Metadata is already loaded from the popup selection, no need to fetch it again
  }

  override ngOnDestroy(): void {
    if (this.thumbnailUrl) {
      this.rockFileLoader.revokeThumbnailUrl(this.thumbnailUrl);
    }
    super.ngOnDestroy();
  }

  runSimulation(): void {
    if (this.isSimulationInputValid()) {
      const directions: Directions = this.simulationDirectionsForm.getRawValue();
      const selectedModel = this.rockModelControl.value as RockModel;
      
      let params = new HttpParams().set('name', this.getSimulationName());
      
      // Add rock model ID and name if available
      if (selectedModel?.id) {
        params = params.set('RockFileModelId', selectedModel.id);
      }
      if (selectedModel?.name) {
        params = params.set('RockFileModelName', selectedModel.name);
      }
      
      const options = {
        headers: new HttpHeaders({
          [COMPONENT_ID_HEADER]: this.componentId,
        }),
        params: params,
      };

      this.loadingService.startLoading(this.componentId);

      this.simulationsService
        .addSimulation(directions, this.fileName.getRawValue(), options, this.componentId)
        .pipe(this.takeUntilDestroyed())
        .subscribe(() => {
          this.snackbarService.openCustom({
            message: 'Simulation started successfully',
            title: 'Success',
            type: 'success',
            action: 'Close',
          });
          this.fileName.patchValue('');
          this.router.navigate(['/dashboard']);
        });
    }
  }

  directionSelected(): boolean {
    const selectedDirections: Directions = this.simulationDirectionsForm.getRawValue();
    return Object.values(selectedDirections).some(Boolean);
  }

  isSimulationInputValid(): boolean {
    return Boolean(
      this.fileName.getRawValue() &&
      this.simulationNameForm.valid &&
      this.directionSelected() &&
      this.rockModelControl.value,
    );
  }

  private getSimulationName(): string {
    const { name } = this.simulationNameForm.getRawValue();
    return name || (this.fileName.getRawValue() ?? '').split('.')[0];
  }

  private initSimulationNameForm(): void {
    this.simulationNameForm = this.fb.group({
      name: ['', Validators.maxLength(256)],
    });
  }

  private initSimulationDirectionsForm(): void {
    this.simulationDirectionsForm = this.fb.group({
      xDirection: [true],
      yDirection: [true],
      zDirection: [true],
    });
  }

  private initFileName(): void {
    this.fileName = new FormControl({ value: '', disabled: true });
  }

  private initRockModelControl(): void {
    this.rockModelControl = new FormControl({ value: null, disabled: true });
  }

  private subscribeToLoadingState(): void {
    this.loadingService
      .getLoadingState(this.componentId)
      .pipe(this.takeUntilDestroyed())
      .subscribe((loadingState: boolean) => {
        this.isLoading = loadingState;
      });
  }
}
